import useSWR, { SWRResponse } from "swr";

import { useSWRApiErrorHandler } from "../common/hooks";
import { DirectorRejectPaymentFormValues } from "../features/payment/hooks/useDirectorRejectPaymentHandler";
import {
    PaginationResponse,
    StateAndRef,
    UUID,
    WorkflowResult,
    Pagination,
} from "../types/common";
import {
    PreAndPaymentStateWithDocuments,
    PreSubmitPaymentState,
    PaymentOrderApprover,
} from "../types/payment";

import request, { ApiError } from "./common";

const path = "/payment-order";

const paymentOrderApi = {
    directorReject: (
        ref: string,
        values: DirectorRejectPaymentFormValues,
    ): Promise<PreSubmitPaymentWorkflowResult> => {
        const payload: PaymentRejectRequest = {
            comment: values.comment,
        };
        return request<PreSubmitPaymentWorkflowResult, PaymentRejectRequest>(
            `/payment/pre/reject/${ref}`,
            "POST",
            payload,
        );
    },
    getPreSubmitPayment: (
        linearId: UUID,
    ): Promise<StateAndRef<PreSubmitPaymentState>> => {
        return request<StateAndRef<PreSubmitPaymentState>>(
            `/payment/pre/linearId/${linearId}/latest`,
        );
    },
};

export const usePaymentOrderListById = (
    invoiceId: string,
): SWRResponse<PreAndPaymentStateWithDocuments[], ApiError> => {
    const onError = useSWRApiErrorHandler();
    return useSWR<PreAndPaymentStateWithDocuments[], ApiError>(
        `${path}/list/${invoiceId}`,
        request,
        { onError },
    );
};

export type usePaymentOrderApproverListResponse = PaginationResponse<
    usePaymentOrderApproverListResponseData[]
>;
export type usePaymentOrderApproverListResponseData = {
    claimId: string;
    mainContractorName: {
        commonName: string;
        country: string;
        locality: string;
        organisation: string;
        organisationUnit: string;
        state: string;
    };
    invoiceId: string;
    paymentOrderId: string;
    projectId: string;
    projectName: string;
    timestamp: string;
};

export const usePaymentOrderApproverList = (
    page: number,
    size: number,
    claimId?: string,
    invoiceId?: string,
    mainContractorName?: string,
    paymentOrderId?: string,
    projectId?: string,
    dateFrom?: number | null,
    dateTo?: number | null,
): SWRResponse<Pagination<PaymentOrderApprover[]>, ApiError> => {
    const params: string[] = [`page=${page}`, `size=${size}`];

    if (claimId) params.push(`claimId=${claimId}`);
    if (invoiceId) params.push(`invoiceId=${invoiceId}`);
    if (mainContractorName)
        params.push(`mainContractorAccountName=${mainContractorName}`);
    if (paymentOrderId) params.push(`paymentOrderId=${paymentOrderId}`);
    if (projectId) params.push(`projectId=${projectId}`);
    if (dateFrom) params.push(`timestampFrom=${dateFrom}`);
    if (dateTo) params.push(`timestampTo=${dateTo + 86399999}`);

    const onError = useSWRApiErrorHandler();
    return useSWR<Pagination<PaymentOrderApprover[]>, ApiError>(
        `${path}/list/approved?${params.join("&")}`,
        request,
        { onError },
    );
};

type PaymentRejectRequest = {
    comment: string;
};

type PreSubmitPaymentWorkflowResult = WorkflowResult<{
    PreSubmitPaymentState: StateAndRef<PreSubmitPaymentState>[];
}>;

export default paymentOrderApi;
